import React from 'react';
import * as Select from '@radix-ui/react-select';
import { ChevronDownIcon, CheckIcon } from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';
import Label from '../primitives/Label';
import { inputCls } from '../primitives/inputClasses';
import { useLocaleStore } from 'stores/useLocaleStore';
import { SUPPORTED_LOCALES } from 'i18n/locales';
import { switchLocale } from 'modules/Common/utils/switchLocale';
import { LAYER } from 'modules/Common/utils/layers';

// Standalone localStorage-backed dropdown for the user's UI language. Not
// bound to react-hook-form — the value lives in zustand and persists across
// reloads, so callers drop this in next to schema-bound fields without
// registering it on the form. Same contract as DecimalConventionField.
export default function LanguageField({ colSpan = 6 }) {
  const { t } = useTranslation('common');
  const locale = useLocaleStore((s) => s.locale);

  const colClass = colSpan === 12 ? 'col-span-12' : 'col-span-12 md:col-span-6';
  const selected = SUPPORTED_LOCALES.find((l) => l.code === locale);

  // switchLocale owns the whole transition: resource load before the store
  // update (no English flash), the app-wide overlay, and the wait for the
  // post-cache-clear refetch wave (permissions included) to settle.
  const onValueChange = (code) => switchLocale(code);

  return (
    <div className={colClass}>
      <div className="flex items-center gap-1.5 mb-1">
        <Label
          htmlFor="uiLanguage"
          className="mb-0"
        >
          {t('preferences.language.label', 'Language')}
        </Label>
      </div>
      <Select.Root
        value={locale ?? 'en'}
        onValueChange={onValueChange}
      >
        <Select.Trigger
          id="uiLanguage"
          className={inputCls(false, 'inline-flex items-center justify-between')}
          aria-label={t('preferences.language.ariaLabel', 'Interface language')}
        >
          {/* Language names are endonyms and are deliberately NOT translated —
              a user who has landed in the wrong language must be able to read
              their way out of it. */}
          <span className="truncate text-left">{selected?.label}</span>
          <Select.Icon>
            <ChevronDownIcon />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Content
            position="popper"
            sideOffset={4}
            style={{
              width: 'var(--radix-select-trigger-width)',
              minWidth: 'var(--radix-select-trigger-width)',
              // Page-anchored popover band from the layering scale — clears
              // the Clerk org-profile host (z-1200) and bootstrap's 10000.
              zIndex: LAYER.pagePopover,
            }}
            className="bg-white rounded-xl shadow-lg border border-slate-200 max-h-[300px] overflow-hidden"
          >
            <Select.Viewport className="p-1">
              {SUPPORTED_LOCALES.map((opt) => (
                <Select.Item
                  key={opt.code}
                  value={opt.code}
                  className="relative flex items-center px-3 py-2 text-sm rounded-md cursor-pointer select-none data-[highlighted]:bg-primaryColor/10 data-[highlighted]:outline-none"
                >
                  <Select.ItemText>{opt.label}</Select.ItemText>
                  <Select.ItemIndicator className="ml-auto pl-3">
                    <CheckIcon />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
      <p className="text-xs text-slate-500 mt-1">
        {t('preferences.language.hint', 'Changes the interface language. Saved on this device.')}
      </p>
    </div>
  );
}
